import { invariant } from '../util/invariant';
import type { FilesystemApis } from './fileOps';
import type { FileApi, FilePath } from './fileApi';
import { UPLOAD_MULTIPART_THRESHOLD } from './limits';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';

export class ReadableFile {
  private fd: number | null = null;

  constructor(readonly title: string, private blob?: Pick<Blob, 'size' | 'slice'>, private native?: {size: number, path: string, fsApis: FilesystemApis}) {
    invariant(blob || native, 'Either blob or native file descriptor needed');
  }

  async getArrayBuffer(start: number, end: number, structuredLogger: StructuredLogger): Promise<ArrayBuffer> {
    const slice = await this.getSlice(start, end, structuredLogger) as Partial<Pick<Blob, 'arrayBuffer'>>;
    const maybe = slice as Partial<Pick<Blob, 'arrayBuffer'>>;
    if (maybe.arrayBuffer) {
      return await maybe.arrayBuffer();
    }
    return slice as ArrayBuffer;
  }

  async getSlice(start: number, end: number, structuredLogger: StructuredLogger): Promise<Blob | ArrayBuffer> {
    const sliceSize = end - start;
    if (sliceSize > UPLOAD_MULTIPART_THRESHOLD) {
      structuredLogger.log({
        level: 'warn',
        message: 'Bad slice size',
        scope: 'ReadableFile/getSlice',
        start,
        end,
        sliceSize,
        hasFd: this.fd != null,
        hasBlob: this.blob != null,
        UPLOAD_MULTIPART_THRESHOLD
      });
      console.warn('Unexpected slice larger than UPLOAD_MULTIPART_THRESHOLD - this is not supported in production!');
    }

    if (this.blob) {
      return this.blob.slice(start, end);
    }

    invariant(this.native, 'Either blob or native file descriptor needed');

    if (!this.fd) {
      this.fd = await this.native.fsApis.preferred.open(this.native.path, 'r');
    }

    let bytesIn: Uint8Array<ArrayBuffer> = new Uint8Array(end - start);
    const { buffer } = await this.native.fsApis.preferred.read(this.fd, bytesIn, 0, end - start, start);
    if (bytesIn !== buffer) {
      bytesIn = buffer as Uint8Array<ArrayBuffer>;
    }

    return this.native.fsApis.canUseBlob ? new Blob([bytesIn]) : bytesIn.buffer;
  }

  get size(): number {
    if (this.blob) {
      return this.blob.size;
    }

    invariant(this.native, 'Either blob or native file descriptor needed');
    return this.native.size;
  }

  async close() {
    if (!this.fd) {
      return;
    }

    invariant(this.native, 'Only native files use fd');
    await this.native.fsApis.preferred.close(this.fd);
    this.fd = null;
  }
}

export function makeReadableFile(file: FileApi, fsApis?: FilesystemApis) {
  let native: {size: number, path: string, fsApis: FilesystemApis} | undefined;
  if (fsApis) {
    // The following is a workaround for getting the full path to files
    // as provided by "select file" dialog in Premiere.
    // If the object has a path property we use that, otherwise use name.
    const pfile: Partial<FilePath> = file;
    const path = pfile.path ?? file.name;
    native = {size: file.size, path, fsApis};
  }

  let blob: Blob | undefined;
  const pblob: Partial<Blob> = file;
  if (pblob.slice != null && pblob.size != null) {
    blob = pblob as Blob;
  }

  invariant(native != null || blob != null, 'File must either be native or blob');
  return new ReadableFile(file.name, blob, native);
}

export const base64StringToUploadableFile = (b64String: string, fileName: string) => {
  const [typeInfo, dataString] = b64String.split(',');
  if (!typeInfo || !dataString) {
    return;
  }

  const regexForMimeType = /:(.*?);/;
  const mimeType = regexForMimeType.exec(typeInfo)?.[1];
  if (!mimeType) {
    return;
  }

  const data = atob(dataString);
  const bytes = new Uint8Array(data.length);

  let index = data.length;
  while (index--) {
    bytes[index] = data.charCodeAt(index);
  }

  return new File([bytes], fileName, { type: mimeType });
};
